import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { rupees, formatIndian, purityColor, formatApiError, formatWeight } from "@/lib/utils";
import { Search, Gem, Lock, User, Eye, X, Grid2x2, List, ChevronUp, ChevronDown, ChevronsUpDown, Share2, Check, Loader2, FileSpreadsheet, FileText, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ProductDetailDrawer from "@/components/ProductDetailDrawer";
import PhotoPlaceholder from "@/components/PhotoPlaceholder";
import MemoBadge from "@/components/MemoBadge";
import { AshthLiteBadge } from "@/components/AshthLiteBadge";
import { PublicDeleteDialog, deletePinErrorMessage } from "@/components/DeletePinControls";

const PUBLIC_API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FranchiseView() {
  const { token, jewelCode: routeJewelCode } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [franchise, setFranchise] = useState(null);
  const [items, setItems] = useState(null);
  const [view, setView] = useState(params.get("view") === "owner" ? "owner" : "staff");
  const [pin, setPin] = useState(() => { localStorage.removeItem(`fpin_${token}`); return localStorage.getItem(`fsess_${token}`) || ""; });
  const [showPin, setShowPin] = useState(false);
  const [pinInput, setPinInput] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(routeJewelCode || null);
  const [crossSearch, setCrossSearch] = useState(null); // {jewel_code, found, in_catalogue}
  const [rateMeta, setRateMeta] = useState(null);
  const [memoFilter, setMemoFilter] = useState("all"); // 'all' | 'memo' | 'sold'
  // CS 21.71 -- Multi-select category filter (chips). Empty set = all.
  // `__uncat__` sentinel matches null/empty category products.
  const [selectedCats, setSelectedCats] = useState(() => new Set());
  const [categoryBuckets, setCategoryBuckets] = useState([]);
  // CS 21.73 -- Products / Designs toggle. Renders only when the franchise
  // holds both record kinds (both flags true on the /public/franchise info
  // response). Default = whichever kind has content; falls back to product.
  const [kind, setKind] = useState("product");
  // CS 21.71 -- Selection set (jewel codes) for bulk share export.
  const [pickedCodes, setPickedCodes] = useState(() => new Set());
  const [shareOpen, setShareOpen] = useState(false);
  // CS 21.86 -- owner-unlocked franchise-link bulk delete (products only;
  // designs have no jewel_code and are excluded).
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleteBusy, setBulkDeleteBusy] = useState(false);
  const canDelete = view === "owner" && !!pin && kind === "product";
  // Grid/list layout, persisted per franchise token
  const LAYOUT_KEY = `flayout_${token}`;
  const [layout, setLayout] = useState(() => localStorage.getItem(LAYOUT_KEY) || "grid");
  useEffect(() => { localStorage.setItem(LAYOUT_KEY, layout); }, [layout, LAYOUT_KEY]);
  // Sort: {key: 'net_weight'|'franchise'|'customer', dir: 'asc'|'desc'} | null
  const [sort, setSort] = useState(null);
  const cycleSort = (key) => {
    setSort((s) => {
      if (!s || s.key !== key) return { key, dir: "asc" };
      if (s.dir === "asc") return { key, dir: "desc" };
      return null;
    });
  };
  const sortedItems = useMemo(() => {
    if (!items) return items;
    // CS 21.77.1 -- POSITIVE-equality guard: the Designs tab must ONLY show
    // records with record_kind === "design"; the Products tab must ONLY show
    // physical stock (record_kind === "product" OR absent → treated as
    // product). NEVER identify by exclusion — that was the reported leak
    // where memo/consignment physical products surfaced under Designs.
    // This is belt-and-suspenders with the backend's record_kind-scoped
    // query and defends against any transient state race, stale render,
    // or reorder that could mix the two kinds in the items array.
    const kindOf = (p) => (p?.record_kind === "design") ? "design" : "product";
    const scoped = items.filter((it) => kindOf(it.product) === kind);
    if (!sort) return scoped;
    const getVal = (it) => {
      switch (sort.key) {
        case "net_weight": return Number(it.product.net_weight) || 0;
        case "dia_wt": return Number(it.pricing?.total_diamond_ct) || 0;
        case "franchise": return Number(it.pricing?.franchise?.total) || 0;
        case "customer": return Number(it.pricing?.customer?.total) || 0;
        case "gp": {
          const cu = Number(it.pricing?.customer?.total) || 0;
          const fr = Number(it.pricing?.franchise?.total) || 0;
          return cu > 0 ? ((cu - fr) / cu) * 100 : 0;
        }
        default: return 0;
      }
    };
    const arr = [...scoped];
    arr.sort((a, b) => {
      const av = getVal(a), bv = getVal(b);
      return sort.dir === "asc" ? av - bv : bv - av;
    });
    return arr;
  }, [items, sort, kind]);

  useEffect(() => {
    axios.get(`${PUBLIC_API}/public/franchise/${token}`).then((r) => {
      setFranchise(r.data);
      // Auto-select whichever kind carries content. If both, keep the current
      // choice; if only designs, switch to design.
      if (r.data?.has_designs && !r.data?.has_products) setKind("design");
    })
      .catch(() => { toast.error("Invalid or inactive link"); setFranchise(false); });
    axios.get(`${PUBLIC_API}/rates/public`).then((r) => setRateMeta(r.data));
  }, [token]);

  // Load products if owner pin works (and reset on view change)
  const loadProducts = async (q = "") => {
    setItems(null);
    try {
      const qs = new URLSearchParams({ view, kind });
      if (view === "owner" && pin) qs.set("pin", pin);
      if (q) qs.set("search", q);
      if (memoFilter === "memo") qs.set("is_memo", "true");
      if (memoFilter === "sold") qs.set("is_memo", "false");
      // CS 21.71 -- category multi-filter passed as CSV
      if (selectedCats.size > 0) qs.set("category", Array.from(selectedCats).join(","));
      const { data } = await axios.get(`${PUBLIC_API}/public/franchise/${token}/products?${qs}`);
      setItems(data.items);
    } catch (e) {
      if (view === "owner" && e.response?.status === 401) {
        // PIN invalid
        setView("staff");
        setPin("");
        localStorage.removeItem(`fsess_${token}`);
        toast.error("PIN incorrect");
      } else {
        toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
      }
    }
  };

  useEffect(() => {
    if (franchise) loadProducts();
  }, [franchise, view, pin, memoFilter, selectedCats, kind]);

  // CS 21.71 -- Refresh the category chips whenever the franchise loads. No
  // PIN or view dependency: category metadata is non-sensitive and same for
  // both roles. CS 21.73 -- categories are kind-scoped too.
  useEffect(() => {
    if (!franchise) return;
    axios.get(`${PUBLIC_API}/public/franchise/${token}/categories?kind=${kind}`)
      .then((r) => setCategoryBuckets(r.data.items || []))
      .catch(() => setCategoryBuckets([]));
  }, [franchise, token, kind]);

  useEffect(() => {
    if (!franchise) return;
    const t = setTimeout(() => loadProducts(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const submitPin = async () => {
    try {
      const { data } = await axios.post(`${PUBLIC_API}/public/franchise/${token}/verify-pin`, { pin: pinInput });
      setPin(data.session);
      localStorage.setItem(`fsess_${token}`, data.session);
      setView("owner");
      setParams({ view: "owner" });
      setShowPin(false);
      setPinInput("");
      toast.success("Owner view unlocked");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Incorrect PIN");
    }
  };

  const switchToStaff = () => {
    setView("staff");
    setPin("");
    localStorage.removeItem(`fsess_${token}`);
    setParams({});
  };

  // CS 21.86 -- bulk delete from the franchise owner link. Fresh delete PIN
  // authorises this one validated batch only; server enforces scope + lockout.
  const doBulkDelete = async (pinValue) => {
    setBulkDeleteBusy(true);
    try {
      const { data } = await axios.post(`${PUBLIC_API}/public/franchise/${token}/products/delete`, {
        jewel_codes: Array.from(pickedCodes),
        pin: pinValue,
      });
      const deleted = (data.deleted || []).length;
      const rejected = (data.rejected_out_of_scope || []).length;
      toast.success(`Deleted ${deleted} piece${deleted === 1 ? "" : "s"}${rejected ? ` · ${rejected} out-of-scope skipped` : ""}`);
      setBulkDeleteOpen(false);
      setPickedCodes(new Set());
      loadProducts(search);
    } catch (e) {
      toast.error(deletePinErrorMessage(e));
    } finally {
      setBulkDeleteBusy(false);
    }
  };

  // direct Jewel-code search across catalogue (any product)
  const doCrossSearch = async (jc) => {
    if (!jc) return;
    try {
      const qs = new URLSearchParams({ view });
      if (view === "owner" && pin) qs.set("pin", pin);
      const { data } = await axios.get(`${PUBLIC_API}/public/franchise/${token}/product/${encodeURIComponent(jc)}?${qs}`);
      setCrossSearch({ data, jewel_code: jc });
    } catch (e) {
      if (e.response?.status === 404) {
        toast.error("Product not found — please check the Jewel Code");
        setCrossSearch({ data: null, jewel_code: jc });
      } else {
        toast.error(formatApiError(e.response?.data?.detail) || "Search failed");
      }
    }
  };

  // CS 21.71 -- Selection helpers (filter-aware Select-All).
  const toggleCat = useCallback((c) => {
    setSelectedCats((prev) => {
      const next = new Set(prev);
      if (next.has(c)) next.delete(c); else next.add(c);
      return next;
    });
    // Changing the filter invalidates the previous selection set — the user
    // is looking at a different slice now.
    setPickedCodes(new Set());
  }, []);
  const clearCats = useCallback(() => {
    setSelectedCats(new Set());
    setPickedCodes(new Set());
  }, []);
  const togglePick = useCallback((jc) => {
    setPickedCodes((prev) => {
      const next = new Set(prev);
      if (next.has(jc)) next.delete(jc); else next.add(jc);
      return next;
    });
  }, []);
  // CS 21.73 -- Design records store `jewel_code = null`; use `style_no` as
  // the selection identity in design mode. Product mode continues to use
  // `jewel_code` verbatim (design mode never leaks into the stock share).
  const identityKey = (product) => kind === "design" ? (product.style_no || "") : (product.jewel_code || "");
  const allFilteredCodes = useMemo(
    () => (items || []).map((it) => identityKey(it.product)).filter(Boolean),
    [items, kind],
  );
  const allPicked = pickedCodes.size > 0 && allFilteredCodes.every((c) => pickedCodes.has(c));
  const toggleSelectAll = useCallback(() => {
    if (allPicked) {
      setPickedCodes(new Set());
    } else {
      // Grab the full filtered set across whatever pagination is in play.
      // (The public products endpoint currently caps at 500; on Excel share
      // the server accepts up to 2000 codes.)
      setPickedCodes(new Set(allFilteredCodes));
    }
  }, [allPicked, allFilteredCodes]);

  if (franchise === null) return <div className="min-h-screen flex items-center justify-center"><div className="skeleton h-6 w-40" /></div>;
  if (franchise === false) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF8F3] p-8">
        <div className="text-center">
          <Lock className="w-12 h-12 mx-auto text-[#C9A84C] mb-4" strokeWidth={1} />
          <h1 className="font-serif text-3xl text-[#0D1B2A]">Link unavailable</h1>
          <p className="text-slate-500 mt-2">This share link is invalid or has been deactivated.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF8F3]" data-testid="franchise-view">
      {/* Header */}
      <header className="glass-nav sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3" data-testid="franchise-brand">
            <img
              src="/brand/ashth_logo.png"
              alt="Ashth"
              className="h-14 w-14 object-contain"
              onError={(e) => { e.currentTarget.style.display = "none"; if (e.currentTarget.nextElementSibling) e.currentTarget.nextElementSibling.style.display = "inline"; }}
            />
            <span className="font-serif text-3xl tracking-[0.3em] text-[#0D1B2A]" style={{ display: "none" }}>APS</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs uppercase tracking-wider text-slate-500 hidden sm:inline">{view === "owner" ? "Owner View" : "Staff View"}</span>
            {view === "staff" ? (
              <Button onClick={() => setShowPin(true)} variant="outline" className="border-[#0D1B2A]/15" data-testid="open-owner-btn">
                <Lock className="w-4 h-4 mr-2" /> Owner View
              </Button>
            ) : (
              <Button onClick={switchToStaff} variant="outline" className="border-[#0D1B2A]/15" data-testid="switch-staff-btn">
                <User className="w-4 h-4 mr-2" /> Staff View
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-16">
        <div className="text-center mb-10">
          <p className="text-xs uppercase tracking-[0.3em] text-[#C9A84C] mb-3">Welcome</p>
          <h1 className="font-serif text-4xl md:text-6xl text-[#0D1B2A]">{franchise.name}</h1>
          {franchise.city && <p className="text-slate-500 mt-2">{franchise.city}</p>}
        </div>

        {/* CS 21.73 -- Products / Designs toggle (shown only when both exist). */}
        {franchise.has_products && franchise.has_designs && (
          <div className="flex items-center justify-center gap-2 mb-8" data-testid="kind-toggle">
            <button
              type="button"
              onClick={() => { setKind("product"); setPickedCodes(new Set()); setSelectedCats(new Set()); }}
              className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${kind === "product" ? "bg-[#0D1B2A] text-white border-[#0D1B2A]" : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"}`}
              data-testid="kind-toggle-products"
            >
              Products
            </button>
            <button
              type="button"
              onClick={() => { setKind("design"); setPickedCodes(new Set()); setSelectedCats(new Set()); }}
              className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${kind === "design" ? "bg-[#0D1B2A] text-white border-[#0D1B2A]" : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"}`}
              data-testid="kind-toggle-designs"
            >
              Designs
            </button>
          </div>
        )}

        {/* Search bar */}
        <div className="luxe-card p-2 max-w-2xl mx-auto flex items-center gap-2 mb-12" data-testid="franchise-search-bar">
          <Search className="w-5 h-5 text-slate-400 ml-3" strokeWidth={1.5} />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Enter Jewel Code or Bag Number…"
            className="border-0 shadow-none focus-visible:ring-0 h-12 text-base bg-transparent"
            data-testid="franchise-search-input"
            onKeyDown={(e) => { if (e.key === "Enter" && search.trim()) doCrossSearch(search.trim()); }}
          />
          <Button onClick={() => doCrossSearch(search.trim())} disabled={!search.trim()} className="bg-[#0D1B2A] hover:bg-[#0D1B2A]/90 text-white h-11" data-testid="franchise-search-btn">
            Lookup
          </Button>
        </div>

        {/* Cross-catalogue search result */}
        {crossSearch && (
          <div className="mb-12 luxe-card p-6" data-testid="cross-search-result">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-[#C9A84C] mb-1">Direct lookup</p>
                {crossSearch.data ? (
                  <>
                    <h3 className="font-serif text-2xl text-[#0D1B2A]">{crossSearch.data.product.jewel_code}</h3>
                    <p className="text-sm text-slate-500 mt-1">{crossSearch.data.product.style_no} · {crossSearch.data.product.category}</p>
                    {!crossSearch.data.in_catalogue && (
                      <p className="text-xs text-amber-700 mt-2 italic">This product is not in your current catalogue</p>
                    )}
                  </>
                ) : (
                  <h3 className="font-serif text-2xl text-[#0D1B2A]">Not found</h3>
                )}
              </div>
              <div className="flex items-center gap-2">
                {crossSearch.data && (
                  <Button onClick={() => setSelected(crossSearch.data.product.jewel_code)} className="bg-[#0D1B2A] text-white">
                    <Eye className="w-4 h-4 mr-2" /> View
                  </Button>
                )}
                <button onClick={() => setCrossSearch(null)} className="p-2 rounded-md text-slate-400 hover:text-[#0D1B2A]"><X className="w-4 h-4" /></button>
              </div>
            </div>
            {crossSearch.data && (
              <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-4 pt-4 border-t border-[#0D1B2A]/5">
                {view === "owner" && crossSearch.data.pricing.franchise && (
                  <Stat label="Your Cost" value={rupees(crossSearch.data.pricing.franchise.total)} />
                )}
                <Stat label="Customer MRP" value={rupees(crossSearch.data.pricing.customer.total)} accent />
              </div>
            )}
          </div>
        )}

        <div className="gold-divider mb-10" />

        {/* CS 21.71 -- Category filter chips (dynamic per-franchise). */}
        {categoryBuckets.length > 0 && (
          <div className="mb-4" data-testid="franchise-category-chips">
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={clearCats}
                data-testid="franchise-category-all"
                className={`text-[11px] px-2.5 py-1 rounded-full border transition ${
                  selectedCats.size === 0
                    ? "bg-[#0D1B2A] text-white border-[#0D1B2A]"
                    : "bg-white text-slate-600 border-[#0D1B2A]/15 hover:border-[#C9A84C]"
                }`}
              >
                All categories
              </button>
              {categoryBuckets.map((b) => {
                const active = selectedCats.has(b.category);
                return (
                  <button
                    key={b.category}
                    type="button"
                    onClick={() => toggleCat(b.category)}
                    data-testid={`franchise-category-chip-${b.category}`}
                    className={`text-[11px] px-2.5 py-1 rounded-full border transition flex items-center gap-1.5 ${
                      active
                        ? "bg-[#C9A84C] text-white border-[#C9A84C]"
                        : "bg-white text-slate-600 border-[#0D1B2A]/15 hover:border-[#C9A84C]"
                    }`}
                  >
                    <span>{b.label}</span>
                    <span
                      className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                        active ? "bg-white/25 text-white" : "bg-[#0D1B2A]/5 text-slate-500"
                      }`}
                    >
                      {b.count}
                    </span>
                  </button>
                );
              })}
              {selectedCats.size > 0 && (
                <button
                  type="button"
                  onClick={clearCats}
                  className="text-[11px] px-2 py-1 rounded-md text-slate-500 hover:text-[#0D1B2A]"
                  data-testid="franchise-category-clear"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        )}

        {/* CS 21.71 -- Filter-aware Select-All + selection count + Share. */}
        {items && items.length > 0 && (
          <div className="mb-4 flex items-center justify-between flex-wrap gap-3"
               data-testid="franchise-selection-bar">
            <div className="flex items-center gap-3 text-xs text-slate-500">
              <button
                type="button"
                onClick={toggleSelectAll}
                data-testid="franchise-select-all"
                className="flex items-center gap-1.5 text-[#0D1B2A] underline-offset-2 hover:underline"
              >
                <span
                  className={`w-4 h-4 rounded border flex items-center justify-center ${
                    allPicked ? "bg-[#0D1B2A] border-[#0D1B2A]" : "bg-white border-slate-400"
                  }`}
                >
                  {allPicked && <Check className="w-3 h-3 text-white" />}
                </span>
                {allPicked ? "Deselect all" : `Select all filtered (${items.length})`}
              </button>
              <span data-testid="franchise-selection-count">
                {pickedCodes.size} of {items.length} selected
              </span>
            </div>
            <div className="flex items-center gap-2">
              {canDelete && (
                <Button
                  onClick={() => setBulkDeleteOpen(true)}
                  disabled={pickedCodes.size === 0}
                  data-testid="franchise-delete-selected-btn"
                  className="bg-red-600 text-white hover:bg-red-700 disabled:opacity-40"
                >
                  <Trash2 className="w-4 h-4 mr-2" /> Delete selected ({pickedCodes.size})
                </Button>
              )}
              <Button
                onClick={() => setShareOpen(true)}
                disabled={pickedCodes.size === 0}
                data-testid="franchise-share-btn"
                className="bg-[#0D1B2A] text-white hover:bg-[#0D1B2A]/90 disabled:opacity-40"
              >
                <Share2 className="w-4 h-4 mr-2" /> Share…
              </Button>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mb-6 gap-3">
          <div className="text-xs uppercase tracking-[0.25em] text-slate-500">
            {items === null ? "Loading…" : `${sortedItems.length} ${kind === "design" ? "design" : "product"}${sortedItems.length === 1 ? "" : "s"}`}
          </div>
          <div className="flex items-center gap-2" data-testid="franchise-view-toggle">
            <Select value={memoFilter} onValueChange={setMemoFilter}>
              <SelectTrigger className="h-9 bg-white border-[#0D1B2A]/15 min-w-[140px]" data-testid="franchise-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All status</SelectItem>
                <SelectItem value="memo">Memo only</SelectItem>
                <SelectItem value="sold">Sold only</SelectItem>
              </SelectContent>
            </Select>
            <button onClick={() => setLayout("grid")} data-testid="franchise-view-grid"
              className={`p-2.5 rounded-md border ${layout === "grid" ? "bg-[#0D1B2A] text-white border-[#0D1B2A]" : "border-[#0D1B2A]/15 text-slate-500 bg-white"}`}>
              <Grid2x2 className="w-4 h-4" strokeWidth={1.5} />
            </button>
            <button onClick={() => setLayout("list")} data-testid="franchise-view-list"
              className={`p-2.5 rounded-md border ${layout === "list" ? "bg-[#0D1B2A] text-white border-[#0D1B2A]" : "border-[#0D1B2A]/15 text-slate-500 bg-white"}`}>
              <List className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>

        {/* Product grid / list */}
        {items === null ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {Array.from({ length: 8 }).map((_, i) => <div key={i} className="skeleton h-72" />)}
          </div>
        ) : items.length === 0 ? (
          <div className="luxe-card p-16 text-center">
            <Gem className="w-10 h-10 mx-auto text-[#C9A84C] mb-3" strokeWidth={1} />
            <h3 className="font-serif text-2xl text-[#0D1B2A]">No products yet</h3>
            <p className="text-slate-500 text-sm">Your catalogue will appear here once products are uploaded.</p>
          </div>
        ) : layout === "grid" ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {sortedItems.map((it) => {
              const idk = identityKey(it.product);
              return (
                <PublicCard
                  key={idk || it.product.id}
                  item={it}
                  view={view}
                  isPicked={idk ? pickedCodes.has(idk) : false}
                  onTogglePick={() => idk && togglePick(idk)}
                  onClick={() => setSelected(it.product.jewel_code || idk)}
                />
              );
            })}
          </div>
        ) : (
          <div className="luxe-card overflow-hidden" data-testid="franchise-list">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#0D1B2A]/3 text-left">
                  <tr className="text-xs uppercase tracking-wider text-slate-500">
                    <th className="px-6 py-4">Jewel Code</th>
                    <th className="px-6 py-4">Style</th>
                    <th className="px-6 py-4 hidden md:table-cell">Category</th>
                    <th className="px-6 py-4">Purity</th>
                    <PublicSortableTh label="Net Wt" sortKey="net_weight" sort={sort} onSort={cycleSort} />
                    <PublicSortableTh label="Dia Wt" sortKey="dia_wt" sort={sort} onSort={cycleSort} />
                    {view === "owner" && <PublicSortableTh label="Your Cost" sortKey="franchise" sort={sort} onSort={cycleSort} />}
                    <PublicSortableTh label="Customer MRP" sortKey="customer" sort={sort} onSort={cycleSort} accent />
                    {view === "owner" && <PublicSortableTh label="GP %" sortKey="gp" sort={sort} onSort={cycleSort} gold />}
                  </tr>
                </thead>
                <tbody>
                  {sortedItems.map((it) => {
                    const cu = Number(it.pricing?.customer?.total) || 0;
                    const fr = Number(it.pricing?.franchise?.total) || 0;
                    const gpPct = cu > 0 ? ((cu - fr) / cu) * 100 : 0;
                    const diaCt = Number(it.pricing?.total_diamond_ct) || 0;
                    return (
                    <tr key={it.product.id || `${it.product.record_kind || "product"}-${it.product.jewel_code}`}
                      onClick={() => setSelected(it.product.jewel_code)}
                      data-testid={`franchise-list-row-${it.product.jewel_code}`}
                      className="border-t border-[#0D1B2A]/5 hover:bg-[#FAF8F3] cursor-pointer">
                      <td className="px-6 py-4 font-medium text-[#0D1B2A] whitespace-nowrap">
                        <div className="inline-flex items-center gap-2">
                          <span>{it.product.jewel_code}</span>
                          {it.product.is_memo && <MemoBadge />}
                          <AshthLiteBadge product={it.product} size="xs" />
                        </div>
                      </td>
                      <td className="px-6 py-4 text-slate-600">{it.product.style_no}</td>
                      <td className="px-6 py-4 text-slate-600 hidden md:table-cell">{it.product.category}</td>
                      <td className="px-6 py-4"><span className={`text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full border ${purityColor(it.product.purity)}`}>{it.product.purity}</span></td>
                      <td className="px-6 py-4 text-right tabular-nums">{formatWeight(it.product.net_weight)} g</td>
                      <td className="px-6 py-4 text-right tabular-nums text-slate-600">{formatIndian(diaCt, { decimals: 3 })} ct</td>
                      {view === "owner" && (
                        <td className="px-6 py-4 text-right tabular-nums text-slate-700">{it.pricing.franchise ? rupees(it.pricing.franchise.total) : "—"}</td>
                      )}
                      <td className="px-6 py-4 text-right tabular-nums font-medium text-[#0D1B2A] border-l-2 border-[#C9A84C]/40 bg-[#C9A84C]/5">{rupees(it.pricing.customer.total)}</td>
                      {view === "owner" && (
                        <td className="px-6 py-4 text-right tabular-nums font-medium text-[#C9A84C]">{gpPct.toFixed(1)}%</td>
                      )}
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {rateMeta && (
          <p className="text-xs text-center text-slate-400 mt-16">
            Prices valid as of {new Date(rateMeta.last_updated_at || Date.now()).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })} · Gold rate {rupees(rateMeta.gold_rate)}/g
          </p>
        )}
      </main>

      {/* PIN dialog */}
      <Dialog open={showPin} onOpenChange={setShowPin}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Owner PIN</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Enter your 4-digit PIN to access franchise-level pricing.</p>
          <Input
            value={pinInput}
            onChange={(e) => setPinInput(e.target.value.replace(/\D/g, "").slice(0, 4))}
            maxLength={4}
            inputMode="numeric"
            placeholder="0000"
            className="text-2xl tracking-[0.5em] text-center h-14"
            data-testid="owner-pin-input"
            onKeyDown={(e) => { if (e.key === "Enter") submitPin(); }}
          />
          <Button onClick={submitPin} disabled={pinInput.length !== 4} className="bg-[#0D1B2A] text-white hover:bg-[#0D1B2A]/90" data-testid="submit-owner-pin">Unlock</Button>
        </DialogContent>
      </Dialog>

      {/* Product detail */}
      {selected && (
        <ProductDetailDrawer
          jewelCode={selected}
          token={token}
          view={view}
          pin={view === "owner" ? pin : undefined}
          kind={kind}
          onDeleted={() => loadProducts(search)}
          onClose={() => setSelected(null)}
        />
      )}

      {/* CS 21.71 -- Bulk share dialog (role-gated). */}
      <FranchiseShareDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        token={token}
        pin={view === "owner" ? pin : undefined}
        role={view === "owner" ? "owner" : "staff"}
        pickedCodes={Array.from(pickedCodes)}
        franchiseName={franchise?.name}
        kind={kind}
      />

      <PublicDeleteDialog
        open={bulkDeleteOpen}
        onOpenChange={setBulkDeleteOpen}
        codes={Array.from(pickedCodes)}
        busy={bulkDeleteBusy}
        onConfirm={doBulkDelete}
        testidPrefix="franchise-bulk-delete"
        scopeNote="Removes the selected pieces from the main Ashth catalogue — they disappear from HO and every share link. HO can restore them from Trash."
      />
    </div>
  );
}

// ─── CS 21.71 -- Franchise-link bulk share dialog ────────────────────
function FranchiseShareDialog({ open, onOpenChange, token, pin, role, pickedCodes, franchiseName, kind }) {
  const [format, setFormat] = useState("excel"); // "excel" | "pdf"
  const [priceOption, setPriceOption] = useState("mrp_only");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // Reset the price option whenever the dialog opens OR the role changes.
    // Staff can never land on `cost_mrp` -- the option is hidden AND the
    // server enforces it.
    if (open) {
      setPriceOption(role === "owner" ? "cost_mrp" : "mrp_only");
      setFormat("excel");
    }
  }, [open, role]);

  const PDF_CAP = 50;
  const count = pickedCodes.length;
  const overPdfCap = format === "pdf" && count > PDF_CAP;

  const submit = async () => {
    if (!count) return;
    if (overPdfCap) return;
    setBusy(true);
    try {
      const endpoint = `/public/franchise/${token}/share/${format}`;
      const body = {
        pin: role === "owner" ? pin : undefined,
        jewel_codes: pickedCodes,
        with_price: priceOption !== "no_prices",
        price_option: priceOption,
        kind: kind || "product",
      };
      const r = await axios.post(`${PUBLIC_API}${endpoint}`, body, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(franchiseName || "franchise").replace(/\s+/g, "_")}_share.${format === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} ready`);
      onOpenChange(false);
    } catch (e) {
      // Blob error responses need a text extraction
      let detail = null;
      const blob = e.response?.data;
      if (blob && typeof blob.text === "function") {
        try { detail = JSON.parse(await blob.text()); } catch { /* not JSON */ }
      } else {
        detail = e.response?.data;
      }
      const status = e.response?.status;
      if (status === 413 && detail?.detail?.error === "pdf_over_cap") {
        toast.error(detail.detail.message || "PDF over cap");
      } else if (status === 403) {
        toast.error(detail?.detail || "Not permitted for this role");
      } else {
        toast.error(formatApiError(detail?.detail) || "Share failed");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange(o); }}>
      <DialogContent className="bg-white" data-testid="franchise-share-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl flex items-center gap-2">
            <Share2 className="w-5 h-5 text-[#C9A84C]" /> Share {count} piece{count === 1 ? "" : "s"}
          </DialogTitle>
        </DialogHeader>

        {/* Format toggle */}
        <div className="space-y-2">
          <div className="text-xs uppercase tracking-wider text-slate-500">Format</div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setFormat("excel")}
              data-testid="franchise-share-format-excel"
              className={`px-3 py-3 rounded-md border flex items-center justify-center gap-2 text-sm ${
                format === "excel" ? "bg-[#0D1B2A] text-white border-[#0D1B2A]" : "border-[#0D1B2A]/15 hover:bg-[#FAF8F3]"
              }`}
            >
              <FileSpreadsheet className="w-4 h-4" /> Excel <span className="text-[10px] opacity-70">(no cap)</span>
            </button>
            <button
              type="button"
              onClick={() => setFormat("pdf")}
              data-testid="franchise-share-format-pdf"
              className={`px-3 py-3 rounded-md border flex items-center justify-center gap-2 text-sm ${
                format === "pdf" ? "bg-[#0D1B2A] text-white border-[#0D1B2A]" : "border-[#0D1B2A]/15 hover:bg-[#FAF8F3]"
              }`}
            >
              <FileText className="w-4 h-4" /> PDF <span className="text-[10px] opacity-70">(cap 50)</span>
            </button>
          </div>
        </div>

        {/* Price options (role-gated) */}
        <div className="space-y-2">
          <div className="text-xs uppercase tracking-wider text-slate-500">Prices</div>
          <div className="space-y-1.5">
            {role === "owner" && (
              <label className="flex items-start gap-2 text-sm cursor-pointer p-2 rounded-md hover:bg-[#FAF8F3]"
                     data-testid="franchise-share-price-cost_mrp">
                <input
                  type="radio"
                  name="price_option"
                  value="cost_mrp"
                  checked={priceOption === "cost_mrp"}
                  onChange={() => setPriceOption("cost_mrp")}
                  className="mt-1"
                />
                <span>
                  <span className="font-medium text-[#0D1B2A]">Franchise Cost + Customer MRP</span>
                  <span className="block text-[11px] text-slate-500">Owner only. Shows your cost plus the customer MRP.</span>
                </span>
              </label>
            )}
            <label className="flex items-start gap-2 text-sm cursor-pointer p-2 rounded-md hover:bg-[#FAF8F3]"
                   data-testid="franchise-share-price-mrp_only">
              <input
                type="radio"
                name="price_option"
                value="mrp_only"
                checked={priceOption === "mrp_only"}
                onChange={() => setPriceOption("mrp_only")}
                className="mt-1"
              />
              <span>
                <span className="font-medium text-[#0D1B2A]">Customer MRP only</span>
                <span className="block text-[11px] text-slate-500">Safe for sharing with customers.</span>
              </span>
            </label>
            <label className="flex items-start gap-2 text-sm cursor-pointer p-2 rounded-md hover:bg-[#FAF8F3]"
                   data-testid="franchise-share-price-no_prices">
              <input
                type="radio"
                name="price_option"
                value="no_prices"
                checked={priceOption === "no_prices"}
                onChange={() => setPriceOption("no_prices")}
                className="mt-1"
              />
              <span>
                <span className="font-medium text-[#0D1B2A]">No prices</span>
                <span className="block text-[11px] text-slate-500">Photos and specs only.</span>
              </span>
            </label>
          </div>
        </div>

        {overPdfCap && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800"
               data-testid="franchise-share-pdf-cap-warning">
            PDF is limited to {PDF_CAP} pieces; narrow your selection or use Excel.
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy || count === 0 || overPdfCap}
            className="bg-[#0D1B2A] text-white hover:bg-[#0D1B2A]/90 disabled:opacity-40"
            data-testid="franchise-share-submit"
          >
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Preparing…</> : "Download"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className={`${accent ? "border-l-2 border-[#C9A84C] pl-3" : ""}`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="font-serif text-xl text-[#0D1B2A] tabular-nums">{value}</div>
    </div>
  );
}

function PublicSortableTh({ label, sortKey, sort, onSort, accent, gold }) {
  const active = sort?.key === sortKey;
  const dir = active ? sort.dir : null;
  const accentCls = accent ? "border-l-2 border-[#C9A84C]/40 bg-[#C9A84C]/5" : "";
  const baseColor = gold ? "text-[#C9A84C]" : "";
  return (
    <th
      onClick={() => onSort(sortKey)}
      data-testid={`franchise-sort-${sortKey}`}
      className={`px-6 py-4 text-right cursor-pointer select-none hover:bg-[#0D1B2A]/5 transition-colors ${accentCls} ${active ? "text-[#C9A84C]" : baseColor}`}
    >
      <span className="inline-flex items-center gap-1 justify-end w-full">
        {label}
        {!active && <ChevronsUpDown className={`w-3 h-3 ${gold ? "text-[#C9A84C]/50" : "text-slate-400"}`} strokeWidth={2} />}
        {active && dir === "asc" && <ChevronUp className="w-3.5 h-3.5 text-[#C9A84C]" strokeWidth={2.5} />}
        {active && dir === "desc" && <ChevronDown className="w-3.5 h-3.5 text-[#C9A84C]" strokeWidth={2.5} />}
      </span>
    </th>
  );
}

function PublicCard({ item, view, onClick, isPicked, onTogglePick }) {
  const p = item.product;
  const pr = item.pricing;
  // CS 21.77 -- designs never have a jewel_code; fall back to style_no so
  // testids are unique per card and card labels are readable.
  const cardKey = p.jewel_code || p.style_no || p.id || "unknown";
  const primaryLabel = p.jewel_code || p.style_no || "—";
  return (
    <div
      className={`luxe-card text-left p-0 overflow-hidden group relative ${
        isPicked ? "ring-2 ring-[#C9A84C]" : ""
      }`}
      data-testid={`public-card-${cardKey}`}
    >
      {/* CS 21.71 -- Selection checkbox overlay (top-left). Stops propagation
          so ticking a card doesn't open the detail drawer. */}
      {onTogglePick && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onTogglePick(); }}
          data-testid={`public-card-pick-${cardKey}`}
          aria-label={isPicked ? "Deselect piece" : "Select piece"}
          className={`absolute top-3 left-3 z-10 w-6 h-6 rounded-md border-2 flex items-center justify-center transition-colors ${
            isPicked
              ? "bg-[#C9A84C] border-[#C9A84C]"
              : "bg-white/95 border-slate-300 hover:border-[#C9A84C]"
          }`}
        >
          {isPicked && <Check className="w-3.5 h-3.5 text-white" />}
        </button>
      )}
      <button
        type="button"
        onClick={onClick}
        className="text-left w-full block"
      >
      <div className="aspect-square bg-[#FAF8F3] flex items-center justify-center overflow-hidden relative">
        {p.photo_url ? (
          <img src={p.photo_url} alt={primaryLabel} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <PhotoPlaceholder />
        )}
        {p.is_memo && <div className="absolute top-3 left-3"><MemoBadge /></div>}
      </div>
      <div className="p-5 space-y-3">
        <div className="flex justify-between items-start">
          <div className="min-w-0">
            <div className="font-medium text-[#0D1B2A] flex items-center gap-1.5 flex-wrap">
              <span>{primaryLabel}</span>
              <AshthLiteBadge product={p} size="xs" />
            </div>
            <div className="text-xs text-slate-500 mt-0.5">{p.jewel_code ? p.style_no : (p.jewel_code_ref || "")}</div>
          </div>
          <span className={`text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full border shrink-0 ${purityColor(p.purity)}`}>{p.purity}</span>
        </div>
        <div className="flex justify-between text-xs text-slate-500">
          <span>{p.category || "Uncategorised"}</span>
          <span className="tabular-nums">{formatWeight(p.net_weight)} g</span>
        </div>
        {(Number(pr?.total_diamond_pcs) > 0 || Number(pr?.total_diamond_ct) > 0) && (
          <div className="flex justify-between text-[11px] text-slate-500 -mt-1" data-testid={`public-card-diamonds-${cardKey}`}>
            <span className="uppercase tracking-wider text-[10px] text-slate-400">Diamonds</span>
            <span className="tabular-nums">
              {Number(pr.total_diamond_pcs) > 0 && `${formatIndian(pr.total_diamond_pcs)} pcs · `}
              {formatIndian(pr.total_diamond_ct, { decimals: 3 })} ct
            </span>
          </div>
        )}
        <div className="pt-3 border-t border-[#0D1B2A]/5">
          {view === "owner" && pr.franchise && (
            <div className="flex justify-between text-xs mb-1.5">
              <span className="text-slate-500">Your Cost</span>
              <span className="tabular-nums text-slate-700">{rupees(pr.franchise.total)}</span>
            </div>
          )}
          <div className="flex justify-between items-baseline border-l-2 border-[#C9A84C] pl-2 -ml-2">
            <span className="text-[11px] uppercase tracking-wider text-[#C9A84C]">Customer MRP</span>
            <span className="font-serif text-xl text-[#0D1B2A] tabular-nums">{rupees(pr.customer.total)}</span>
          </div>
        </div>
      </div>
      </button>
    </div>
  );
}
